import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCatalog } from '../context/CatalogContext';
import AddressAutocomplete from './AddressAutocomplete';
import SchoolPicker from './SchoolPicker';
import SchoolLevelSection from './SchoolLevelSection';
import { SectionCap, Field, Pill, EditorialButton } from './Editorial';
import EditorField from './athleteEditor/EditorField';
import AddressRegionFields from './AddressRegionFields';
import SectionIndex from './athleteEditor/SectionIndex';
import MeasurablesLedger from './athleteEditor/MeasurablesLedger';
import AcademicScoresFields from './athleteEditor/AcademicScoresFields';
import AccoladesEditor from './athleteEditor/AccoladesEditor';
import TrackMarksEditor from './athleteEditor/TrackMarksEditor';
import ResultEntriesEditor from './athleteEditor/ResultEntriesEditor';
import MatchRecordEditor from './athleteEditor/MatchRecordEditor';
import EquestrianResultsEditor from './athleteEditor/EquestrianResultsEditor';
import CredentialsEditor from './athleteEditor/CredentialsEditor';
import SeasonStatsEditor from './athleteEditor/SeasonStatsEditor';
import OffersVisitsCard from './athleteEditor/OffersVisitsCard';
import { cardLabel } from './athleteEditor/RailCards';
import './Editorial.css';
import './athleteEditor/AthleteEditor.css';
import Button from './Button';

/*
 * AdminAthleteEditPanel — lets an admin / internal_staff user edit ALL the
 * fields a player can fill in, for any athlete, from inside the admin page.
 * The admin stays in admin context: every write carries `as_athlete_id={userId}`
 * explicitly (we do NOT enter the global ActingAthleteContext). The backend
 * grants admins edit permission for that target and records each change in
 * admin_profile_edit_logs.
 *
 * Save status (autosave timestamp / error) is hoisted to the page via
 * `onSaveStatus` and rendered by the IdentityBar's edit-mode chip — this
 * panel no longer owns its own toast banner or header.
 */

// Adapter so AddressRegionFields renders inside this panel's EditorField chrome.
const RegionField = ({ label, empty, children }) => (
  <EditorField label={label} state={empty ? 'empty' : 'value'}>{children}</EditorField>
);

const MAJORS = [
  { value: 'biology', label: 'Biology' },
  { value: 'business', label: 'Business' },
  { value: 'communications', label: 'Communications' },
  { value: 'computer_science', label: 'Computer Science' },
  { value: 'education', label: 'Education' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'health', label: 'Health' },
  { value: 'psychology', label: 'Psychology' },
  { value: 'social_sciences', label: 'Social Sciences' },
  { value: 'visual_performing_arts', label: 'Visual & Performing Arts' },
];
const REGIONS = [
  { value: 'west', label: 'West' }, { value: 'midwest', label: 'Midwest' },
  { value: 'northeast', label: 'Northeast' }, { value: 'southwest', label: 'Southwest' },
  { value: 'southeast', label: 'Southeast' },
];
const DIVISIONS = [
  { value: 'd1', label: 'D1' }, { value: 'd2', label: 'D2' }, { value: 'd3', label: 'D3' },
  { value: 'naia', label: 'NAIA' }, { value: 'juco', label: 'JUCO' },
];
const SCHOOL_SIZES = [
  { value: 'small', label: 'Small (<5k)' }, { value: 'medium', label: 'Medium (5–15k)' },
  { value: 'large', label: 'Large (15k+)' },
];
const CAMPUS_SETTINGS = [
  { value: 'urban', label: 'Urban' }, { value: 'suburban', label: 'Suburban' }, { value: 'rural', label: 'Rural' },
];
const SCHOOL_TYPES = [
  { value: 'any', label: 'No preference' }, { value: 'public', label: 'Public' }, { value: 'private', label: 'Private' },
];
const HOBBIES = [
  { value: 'gaming', label: 'Gaming' }, { value: 'music', label: 'Music' },
  { value: 'social_media', label: 'Social media' }, { value: 'fitness_gym', label: 'Fitness' },
  { value: 'reading_audiobooks', label: 'Reading' }, { value: 'watching_sports', label: 'Watching sports' },
  { value: 'photography_video', label: 'Photography' }, { value: 'cooking_food', label: 'Cooking' },
  { value: 'outdoor_activities', label: 'Outdoor activities' }, { value: 'collecting_building', label: 'Collecting' },
];
const SOCIALS = [
  ['twitter_handle', 'Twitter / X'], ['instagram_handle', 'Instagram'], ['youtube_handle', 'YouTube'],
  ['tiktok_handle', 'TikTok'], ['snapchat_handle', 'Snapchat'], ['facebook_handle', 'Facebook'],
  ['linkedin_handle', 'LinkedIn'], ['whatsapp_number', 'WhatsApp'],
];
const VIDEO_CATEGORIES = [
  ['personal_statement', 'Personal Statement'], ['personal', 'Highlight Reel'],
  ['in_season', 'Game Film'], ['off_season', 'Off-Season'],
  ['combine', 'Combine'], ['verification', 'Verification'],
];
const DOC_CATEGORIES = [['transcript', 'Transcript'], ['test_score', 'Test Score Report']];

// Membership end dates are contracts and subscriptions — a year outside this
// window is a half-typed date input (0002-, 0027-), never a real end date.
const MIN_YEAR = 2000;
const MAX_YEAR = 2100;
function isPlausibleDate(v) {
  const m = /^(\d{4})-\d{2}-\d{2}$/.exec(v || '');
  return !!m && Number(m[1]) >= MIN_YEAR && Number(m[1]) <= MAX_YEAR;
}

// Resolves a sport's saved coach-contact row purely from its inputs — no
// closure over component state — so it's safe to call from render, from a
// useCallback body, or anywhere else without an exhaustive-deps entanglement.
// Matches by catalog NAME -> slug -> row.sport_slug, never by falling back to
// a default slug: an unresolvable sportName returns null rather than
// guessing, so it can never return another sport's row.
function matchedSportCoachRow(catalogSports, sportCoaches, sportName) {
  const sportEntry = (catalogSports || []).find(s => s.name === sportName);
  if (!sportEntry) return null;
  return (sportCoaches || []).find(r => r.sport_slug === sportEntry.slug) || null;
}

// Section index — stable ids/labels per the spec. Membership is dropped when
// the caller has no membership object to show (mirrors the old conditional
// membership card).
const ALL_SECTIONS = [
  { id: 'membership', label: 'Membership' },
  { id: 'contact', label: 'Contact' },
  { id: 'hs-coach', label: 'Coach Contacts' },
  { id: 'guardians', label: 'Guardians' },
  { id: 'academics', label: 'Academics' },
  { id: 'recruiting', label: 'Recruiting' },
  { id: 'social', label: 'Social' },
  { id: 'photos', label: 'Photos & Video' },
  { id: 'documents', label: 'Documents' },
  { id: 'accolades', label: 'Accolades' },
  { id: 'stats', label: 'Stats' },
];

// Scroll-spy: watches each section element and reports the topmost one that's
// currently intersecting the viewport as the active section index item.
function useActiveSection(ids) {
  const [activeId, setActiveId] = useState(ids[0]);
  const idsKey = ids.join('|');
  useEffect(() => {
    const els = ids.map(id => document.getElementById(id)).filter(Boolean);
    if (!els.length) return undefined;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter(e => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible.length) setActiveId(visible[0].target.id);
    }, { rootMargin: '-120px 0px -70% 0px', threshold: [0, 1] });
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [idsKey]);
  return [activeId, setActiveId];
}

export default function AdminAthleteEditPanel({ userId, profile: initialProfile, videos: initialVideos, cards = [], membership, perms, logs, getConfig, onMembershipChange, onClose, onSaved, onSportCoachSaved, onSaveStatus }) {
  const { API_URL } = useAuth();
  // Membership writes are staff-only on the server (POST /admin/users/:id/membership)
  // regardless of what perms says elsewhere on this page — fail CLOSED: an absent
  // `perms` prop, or one missing this key, must NOT unlock the section.
  const canEditMembership = perms?.canEditMembership === true;
  const [form, setForm] = useState(initialProfile || {});
  const catalog = useCatalog();
  const [videos, setVideos] = useState(initialVideos || []);
  const [docs, setDocs] = useState([]);
  const [campPhotos, setCampPhotos] = useState({});
  // Pasted-but-not-yet-added video URLs, keyed by category.
  const [videoDrafts, setVideoDrafts] = useState({});
  const [compEnd, setCompEnd] = useState('');
  // Contract End Date while it is being edited — null means "not editing, show
  // the server's value". Chrome's date input fires a change on every year digit
  // typed (0002-…, 0020-…, 0202-…, 2027-…), so saving on change wrote those
  // partial years to prod and the server echo reset the half-typed year. It
  // saves once, on blur.
  const [contractEndDraft, setContractEndDraft] = useState(null);
  const [compPlan, setCompPlan] = useState('player_annual');
  // Account email lives on the User (not the AthleteProfile), so it saves via
  // the admin user-update endpoint rather than the profile autosave. UID — not
  // email — is the stable identifier, so changing it is safe.
  const [accountEmail, setAccountEmail] = useState(initialProfile?.user?.email || '');

  // Athlete name — admin/internal_staff, or a partner on an athlete they own
  // (server-enforced; PUT /athletes/:id/name 404s anyone else). Saved on blur.
  const [nameDraft, setNameDraft] = useState({
    first: initialProfile?.user?.first_name || '', last: initialProfile?.user?.last_name || '' });

  // Per-sport HS + club coach contact — `coachDrafts` holds ONLY fields the
  // admin has actually typed into during this mount, keyed by the sport's
  // DISPLAY NAME (never a resolved slug — see matchedSportCoachRow's comment
  // for why). It starts empty and is never seeded from the server: seeding a
  // snapshot at mount raced CatalogContext's async `/sports/catalog` fetch
  // (CatalogContext.js) — if the catalog lost that race, a real saved coach
  // row for a non-football sport got silently mapped to nothing and the
  // block rendered blank even though the server had the data. Instead, the
  // displayed value and the diff baseline are both *derived at render* from
  // `form.sport_coaches` (already present on `form` — it's part of the
  // profile payload) via matchedSportCoachRow, resolved against whatever
  // `catalog.sports` looks like *right now*. The moment the catalog finishes
  // loading, the very next render matches correctly — no extra effect, no
  // stale snapshot, nothing to re-fetch.
  const [coachDrafts, setCoachDrafts] = useState({});

  const sections = membership ? ALL_SECTIONS : ALL_SECTIONS.filter(s => s.id !== 'membership');
  const sectionIds = sections.map(s => s.id);
  const [activeSection, setActiveSection] = useActiveSection(sectionIds);

  // 'form' = the scroll-spy section form; 'measurables' = the verify ledger
  // swapped into the body (reached via the separated nav item below the
  // sections). The form stays mounted (display:none) so the scroll-spy observer
  // keeps its element refs; only the ledger mounts/unmounts.
  const [view, setView] = useState('form');
  const onJump = useCallback((id) => {
    setView('form');
    // If we're returning from the ledger the form was display:none; wait a frame
    // for it to lay out before scrolling to the target section.
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    });
  }, [setActiveSection]);

  // Every admin write carries as_athlete_id so the backend targets this athlete.
  const cfg = useCallback((extra = {}) => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, ...(extra.headers || {}) },
    params: { as_athlete_id: userId, ...(extra.params || {}) },
  }), [userId]);

  // Save status is hoisted to the IdentityBar (no local toast banner). Every
  // successful save reports `savedAt`; every failure reports `error`.
  const reportStatus = useCallback((msg, isErr = false) => {
    if (!onSaveStatus) return;
    if (isErr) onSaveStatus({ error: msg });
    else onSaveStatus({ savedAt: Date.now() });
  }, [onSaveStatus]);

  const refreshVideos = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/videos/player/${userId}`, cfg());
      setVideos(Array.isArray(r.data) ? r.data : []);
    } catch { /* ignore */ }
  }, [API_URL, userId, cfg]);

  const refreshDocs = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/athletes/documents`, cfg());
      setDocs(Array.isArray(r.data) ? r.data : []);
    } catch { /* ignore */ }
  }, [API_URL, userId, cfg]);

  useEffect(() => { refreshDocs(); }, [refreshDocs]);

  const refreshCampPhotos = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/athletes/${userId}/camp-photos`, cfg());
      setCampPhotos(r.data || {});
    } catch { setCampPhotos({}); }
  }, [API_URL, userId, cfg]);
  useEffect(() => { refreshCampPhotos(); }, [refreshCampPhotos]);

  // ── Saving ──────────────────────────────────────────────────────────
  const saveField = useCallback(async (name, value) => {
    try {
      await axios.post(`${API_URL}/athletes/profile`, { [name]: value }, cfg());
      reportStatus('Saved');
      if (onSaved) onSaved({ [name]: value });
    } catch (e) {
      reportStatus(e.response?.data?.detail || 'Error saving', true);
    }
  }, [API_URL, cfg, reportStatus, onSaved]);

  // Persist several profile fields in one request (used by the school picker,
  // which fills name + city + state + zip together on a suggestion tap).
  const saveFields = useCallback(async (patch) => {
    try {
      await axios.post(`${API_URL}/athletes/profile`, patch, cfg());
      reportStatus('Saved');
      if (onSaved) onSaved(patch);
      return true;
    } catch (e) {
      reportStatus(e.response?.data?.detail || 'Error saving', true);
      return false;
    }
  }, [API_URL, cfg, reportStatus, onSaved]);

  // High school <-> college. The server clears the other side's fields on a
  // level switch, so mirror that locally once the save succeeds.
  const saveSchoolLevel = useCallback(async (fields) => {
    const ok = await saveFields(fields);
    if (ok) {
      const cleared = fields.school_level === 'college'
        ? { high_school: '', high_school_city: '', high_school_state: '', high_school_zip: '' }
        : fields.school_level === 'high_school'
          ? { college_name: null, college_unitid: null, eligibility_years_remaining: null }
          : {};
      setForm(prev => ({ ...prev, ...cleared, ...fields }));
    }
    return ok;
  }, [saveFields]);

  const confirmLeaveCollege = useCallback(() => Promise.resolve(window.confirm(
    'Switch this athlete back to high school? This removes their college and years of eligibility.',
  )), []);

  // ── Per-sport coach contacts ──────────────────────────────────────────
  // "The athlete's sports" = primary sport + other sports played, both
  // stored as display names (multi-sport standing rule — no hardcoded list).
  const athleteSports = useMemo(() => {
    const seen = new Set();
    const out = [];
    [form.sport, ...(Array.isArray(form.sports_played) ? form.sports_played : [])].forEach(name => {
      const trimmed = (name || '').trim();
      if (trimmed && !seen.has(trimmed)) { seen.add(trimmed); out.push(trimmed); }
    });
    return out;
  }, [form.sport, form.sports_played]);

  // Whether the catalog actually recognizes this display-name string. A
  // sport that doesn't resolve gets its block disabled (see the "hs-coach"
  // section below) rather than silently keyed under some other sport.
  const isCatalogSport = (sportName) => catalog.sports.some(s => s.name === sportName);

  const setCoachLocal = (sportName, field, value) => {
    setCoachDrafts(prev => ({ ...prev, [sportName]: { ...(prev[sportName] || {}), [field]: value } }));
  };

  // Saved on the athlete's behalf via as_athlete_id (cfg()) — same pattern as
  // every other write in this panel, keeping the admin in admin context
  // rather than entering the global ActingAthleteContext. The diff baseline
  // is the matched server row resolved fresh from `form.sport_coaches` (not
  // a mount-time snapshot — see the coachDrafts comment above), so a stale
  // catalog can never make this compare against wrong/missing data.
  //
  // The success merge uses the functional setForm updater's `prev` — never
  // the closure-captured `form` above — so it always merges onto whatever
  // is truly the latest committed form state. That matters because an admin
  // can tab through several coach fields faster than a round trip: two
  // blurs fired before either axios call resolves share the SAME
  // `saveCoachField` closure snapshot of `form.sport_coaches`. Building
  // `nextList` from that captured snapshot (rather than from `prev` inside
  // the updater) would mean whichever request resolves second overwrites
  // form.sport_coaches with a list missing the first request's merged row —
  // a lost update where the server has both saves correctly but the local
  // form (and the rail) silently revert the first one.
  //
  // Parent propagation uses the SAME shape: we hand `onSportCoachSaved` the
  // single authoritative row the server just returned (it carries
  // `sport_slug` plus all seven fields) and let AdminAthleteProfilePage merge
  // it into its own `profile` via its own `prev`. Deliberately NOT an effect
  // watching `form.sport_coaches`: `onSaved`/`onSportCoachSaved` are inline
  // arrows in the parent, so such an effect re-fires on every parent render,
  // and the parent's merge always produces a fresh object (React never bails
  // out), which re-renders the parent — an unbounded loop that the parent's
  // 1s edit-mode force-tick starts on its own with no user action. Firing
  // only from an actual save, with a value that doesn't depend on any local
  // list, removes both the loop and the lost-update race at once: two racing
  // saves each carry only their own row, and the parent composes them.
  const saveCoachField = useCallback(async (sportName, field, value) => {
    const matched = matchedSportCoachRow(catalog.sports, form.sport_coaches, sportName);
    const baselineVal = (matched && matched[field]) || '';
    if ((value || '') === baselineVal) return; // unchanged — send no request at all
    try {
      const res = await axios.put(`${API_URL}/athletes/profile/sport-coaches`, { sport: sportName, [field]: value }, cfg());
      setForm(prev => ({
        ...prev,
        sport_coaches: [
          ...(prev.sport_coaches || []).filter(r => r.sport_slug !== res.data.sport_slug),
          res.data,
        ],
      }));
      if (onSportCoachSaved) onSportCoachSaved(res.data);
      reportStatus('Saved');
    } catch (e) {
      reportStatus(e.response?.data?.detail || 'Error saving coach contact', true);
    }
  }, [API_URL, cfg, reportStatus, catalog.sports, form.sport_coaches, onSportCoachSaved]);

  const coachField = (sportName, field, label, type = 'text') => {
    const draft = coachDrafts[sportName];
    const val = draft && draft[field] !== undefined
      ? draft[field]
      : ((matchedSportCoachRow(catalog.sports, form.sport_coaches, sportName) || {})[field] || '');
    return (
      <EditorField label={label} state={val ? 'value' : 'empty'}>
        <input className="ae-field__input" type={type} value={val}
          onChange={e => setCoachLocal(sportName, field, e.target.value)}
          onBlur={e => saveCoachField(sportName, field, e.target.value)} />
      </EditorField>
    );
  };

  // Account email save — hits the admin user-update endpoint (validates format,
  // uniqueness, and blocks OAuth/Google/Apple accounts). No-ops if unchanged.
  const saveEmail = useCallback(async () => {
    const next = (accountEmail || '').trim();
    const current = initialProfile?.user?.email || '';
    if (!next || next === current) { setAccountEmail(current); return; }
    try {
      await axios.put(`${API_URL}/admin/users/${userId}`, { email: next },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      reportStatus('Email updated');
      if (onSaved) onSaved({ user: { ...(initialProfile?.user || {}), email: next } });
    } catch (e) {
      reportStatus(e.response?.data?.detail || 'Error updating email', true);
      setAccountEmail(current);
    }
  }, [API_URL, userId, accountEmail, initialProfile, reportStatus, onSaved]);

  const saveName = useCallback(async () => {
    const first = nameDraft.first.trim(); const last = nameDraft.last.trim();
    const cur = initialProfile?.user || {};
    if (first === (cur.first_name || '') && last === (cur.last_name || '')) return;
    if (!first || !last) {
      reportStatus('First and last name are required', true);
      setNameDraft({ first: cur.first_name || '', last: cur.last_name || '' });
      return;
    }
    try {
      await axios.put(`${API_URL}/athletes/${userId}/name`, { first_name: first, last_name: last }, cfg());
      reportStatus('Name updated');
      if (onSaved) onSaved({ user: { ...cur, first_name: first, last_name: last } });
    } catch (e) {
      reportStatus(e.response?.data?.detail || 'Error updating name', true);
      setNameDraft({ first: cur.first_name || '', last: cur.last_name || '' });
    }
  }, [API_URL, cfg, userId, nameDraft, initialProfile, reportStatus, onSaved]);

  // Membership/subscription (admin endpoint, keyed by path user_id — no as_athlete_id)
  const postMembership = useCallback(async (patch) => {
    try {
      const r = await axios.post(`${API_URL}/admin/users/${userId}/membership`, patch,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (onMembershipChange) onMembershipChange(r.data);
      reportStatus('Membership updated');
      return r.data;
    } catch (e) {
      reportStatus(e.response?.data?.detail || 'Membership update failed', true);
      return null;
    }
  }, [API_URL, userId, onMembershipChange, reportStatus]);

  // Concierge manager (owner) — same endpoint the Concierge page's own
  // dropdown writes to (PUT /concierge/:id/owner). Picking a manager for an
  // athlete who is not yet a concierge member also turns membership on first
  // (owner-approved design), so the checkbox and this selector never disagree
  // about whether the athlete is a client. Clearing the manager leaves
  // membership alone. `base` is the freshest membership object available —
  // the just-turned-on-membership response when we had to make one, otherwise
  // the prop — so the merged object we hand back never reverts is_concierge
  // to a value from before this action ran.
  const changeConciergeOwner = useCallback(async (e) => {
    const raw = e.target.value;
    const owner_user_id = raw === '' ? null : Number(raw);
    let base = membership;
    if (owner_user_id !== null && !membership?.is_concierge) {
      const updated = await postMembership({ is_concierge: true });
      if (!updated) return; // postMembership already reported the failure
      base = updated;
    }
    try {
      const r = await axios.put(`${API_URL}/concierge/${userId}/owner`, { owner_user_id },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (onMembershipChange) onMembershipChange({ ...base, concierge_owner: r.data.owner || null });
      if (r.data.sms_cell_removal === 'failed') {
        reportStatus("Manager changed, but the previous manager's phone could not be removed from the family's text thread. Try again or contact support.", true);
      } else {
        reportStatus('Manager updated');
      }
    } catch (err) {
      reportStatus(err.response?.data?.detail || 'Failed to update manager', true);
    }
  }, [API_URL, userId, membership, onMembershipChange, reportStatus, postMembership]);

  const commitContractEnd = () => {
    const draft = contractEndDraft;
    setContractEndDraft(null);
    if (draft === null || draft === (membership?.concierge_expires_at || '')) return;
    if (draft && !isPlausibleDate(draft)) {
      reportStatus(`Contract End Date year must be between ${MIN_YEAR} and ${MAX_YEAR}`, true);
      return;
    }
    postMembership({ concierge_expires_at: draft || null });
  };

  // Grant a comp sub — warn first if it would override an active paid sub.
  const applyComp = () => {
    if (!compEnd) { reportStatus('Pick an end date', true); return; }
    if (!isPlausibleDate(compEnd)) {
      reportStatus(`Comp end date year must be between ${MIN_YEAR} and ${MAX_YEAR}`, true);
      return;
    }
    const sub = membership && membership.subscription;
    const REAL = ['stripe', 'apple', 'google'];
    if (sub && sub.status === 'active' && REAL.includes(String(sub.payment_platform || '').toLowerCase())) {
      const ok = window.confirm(
        `This athlete has an ACTIVE ${sub.payment_platform} subscription` +
        `${sub.plan_type ? ` (${sub.plan_type})` : ''}${sub.current_period_end ? `, ending ${sub.current_period_end}` : ''}.\n\n` +
        `Setting a comp subscription will override it — status becomes comp, ending ${compEnd}. ` +
        `Their billing IDs are preserved, but the app will treat them as complimentary.\n\nContinue?`
      );
      if (!ok) return;
    }
    postMembership({ set_comp_subscription: true, subscription_end_date: compEnd, plan_type: compPlan });
  };

  const setLocal = (name, value) => setForm(prev => ({ ...prev, [name]: value }));
  const onText = (name) => (e) => setLocal(name, e.target.value);
  const onBlurSave = (name) => () => saveField(name, form[name] ?? '');

  const toggleMulti = (name, value) => {
    const cur = Array.isArray(form[name]) ? form[name] : [];
    const next = cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value];
    setLocal(name, next);
    saveField(name, next);
  };
  const setSingle = (name, value) => { setLocal(name, value); saveField(name, value); };

  // ── Sport & positions (catalog-driven) ──────────────────────────────
  // profile.sport stores the DISPLAY NAME, not the slug — the search filters
  // match on the stored name (athletes.py), so the select's value is the slug
  // but what we persist is `name`. Same resolution MySportsPage.js uses.
  const currentSportSlug =
    (catalog.sports.find(s => s.name === form.sport) || {}).slug || 'football';
  const positionNoun = (catalog.getSport(currentSportSlug) || {}).position_noun || 'Position';

  // Stats section — mirrors ios/…/Profile/ProfileSectionDestination.swift:
  // equestrian keeps a show record (a distinct ledger, not a stats_mode), and
  // everything else routes off the catalog's stats_mode. No sport is hardcoded
  // beyond that one named case, which iOS and the read-view cards special-case
  // the same way.
  const statsMode = catalog.statsModeFor(currentSportSlug);
  const isEquestrian = currentSportSlug === 'equestrian';
  const statsSectionLabel = isEquestrian ? 'Show Record & Credentials'
    : statsMode === 'event_marks' ? 'Marks'
    : statsMode === 'result_entries' ? 'Results'
    : statsMode === 'match_record' ? 'Matches'
    : 'Season Stats';
  const statsEditor = isEquestrian ? (
    <>
      <EquestrianResultsEditor getConfig={cfg} />
      <CredentialsEditor getConfig={cfg} />
    </>
  ) : statsMode === 'event_marks' ? <TrackMarksEditor getConfig={cfg} />
    : statsMode === 'result_entries' ? <ResultEntriesEditor sportSlug={currentSportSlug} getConfig={cfg} />
    : statsMode === 'match_record' ? <MatchRecordEditor sportSlug={currentSportSlug} getConfig={cfg} />
    : <SeasonStatsEditor
        sportSlug={currentSportSlug}
        positions={[form.position_primary, form.position_secondary, form.position_other].filter(Boolean)}
        getConfig={cfg} />;

  // Changing sport invalidates the position fields — they come from the old
  // sport's catalog and would be meaningless (and unsearchable) under the new
  // one. Cleared in the same request so the profile is never half-migrated.
  const handleSportChange = (slug) => {
    const next = catalog.sports.find(s => s.slug === slug);
    if (!next) return;
    const hadPositions = form.position_primary || form.position_secondary || form.position_other;
    if (hadPositions && !window.confirm(
      `Changing the sport to ${next.name} will clear this athlete's selected positions. Continue?`)) {
      return; // select reverts on the next render
    }
    const patch = { sport: next.name, position_primary: '', position_secondary: '', position_other: '' };
    setForm(prev => ({ ...prev, ...patch }));
    saveFields(patch);
  };

  // "Other sports" are slots in the sports_played array — the same field the
  // mobile editors and MySportsPage write. Stored as display names.
  const otherSports = Array.isArray(form.sports_played) ? form.sports_played : [];
  const setOtherSport = (index, name) => {
    const next = [...otherSports];
    next[index] = name;
    const cleaned = next.filter(v => v && v.trim());
    setLocal('sports_played', cleaned);
    saveField('sports_played', cleaned);
  };

  // Value is the sport NAME here (sports_played stores names). Excludes the
  // primary sport and any sport already taken by another slot.
  const otherSportSelect = (index) => {
    const value = otherSports[index] || '';
    const taken = otherSports.filter((_, k) => k !== index);
    return (
      <EditorField label={`Other Sport ${index + 1}`} state={value ? 'value' : 'empty'}>
        <select className="ae-field__input" value={value}
          onChange={e => setOtherSport(index, e.target.value)}>
          <option value="">Select...</option>
          {catalog.sports
            .filter(s => s.name === value || (s.name !== form.sport && !taken.includes(s.name)))
            .map(s => <option key={s.slug} value={s.name}>{s.name}</option>)}
        </select>
      </EditorField>
    );
  };

  // Options carry the position LABEL as the value, grouped by the catalog's
  // group_label — same contract MySportsPage.js writes, so both surfaces store
  // identical strings.
  const positionSelect = (label, field) => (
    <EditorField label={label} state={form[field] ? 'value' : 'empty'}>
      <select className="ae-field__input" value={form[field] || ''} onChange={e => setSingle(field, e.target.value)}>
        <option value="">Select {positionNoun.toLowerCase()}...</option>
        {catalog.groupedPositionsFor(currentSportSlug).map(g => (
          g.group ? (
            <optgroup key={g.group} label={g.group}>
              {g.positions.map(pos => <option key={pos.slug} value={pos.label}>{pos.label}</option>)}
            </optgroup>
          ) : g.positions.map(pos => <option key={pos.slug} value={pos.label}>{pos.label}</option>)
        ))}
      </select>
    </EditorField>
  );

  // School autocomplete, WITH a guardrail the athlete-facing surfaces do not
  // have. SchoolPicker emits { name, city, state, zip } on both typing and
  // picking; city/state/zip are populated ONLY when a suggestion is tapped.
  //
  // Three outcomes, in order:
  //
  //   1. Tapped a suggestion  -> save name + city + state + zip together.
  //   2. Typed anything -> DO NOT SAVE. Hold it locally and require either a
  //      pick (case 1) or an explicit "Save anyway".
  //
  // Typing never commits. It used to: a typed name that uniquely matched a
  // school was treated as a pick, saved, and written back into the box. But
  // "Penn" is a complete school name AND the start of "Penn Hills", so typing
  // it saved the wrong school and swallowed the next space (3,791 names are a
  // unique match and also the start of a longer one). W3C's combobox pattern
  // and Downshift / React Aria all keep typed text and the selected item as
  // separate state for this reason: selection is an act, not a side effect.
  //
  // Case 2 is the guardrail. It used to debounce-save the raw text after 700ms,
  // which is how an unrecognised school became permanent without anyone
  // deciding to keep it. This is admin-only on purpose: a hard stop is safe
  // here because admins are not the signup funnel, and an athlete blocked
  // mid-registration is a lost athlete. `exactMatch` returns null on an
  // ambiguous name (same school name in two states), so an ambiguous name
  // falls to case 3 rather than guessing a state.
  const schoolSaveTimer = useRef(null);
  const [schoolWarning, setSchoolWarning] = useState(null);
  useEffect(() => () => { if (schoolSaveTimer.current) clearTimeout(schoolSaveTimer.current); }, []);

  // The last school name actually SAVED (form.high_school also holds held, typed text).
  const savedSchool = useRef(initialProfile?.high_school || '');
  // Optimistic, but a failed save rolls the school fields back to what the server
  // still holds (the saved name, and the previous city/state/zip).
  const persistSchool = async (patch) => {
    const prevSaved = savedSchool.current;
    const prevForm = {
      high_school_city: form.high_school_city, high_school_state: form.high_school_state,
      high_school_zip: form.high_school_zip,
    };
    savedSchool.current = patch.high_school;
    setForm(prev => ({ ...prev, ...patch }));
    const ok = await saveFields(patch);
    if (ok === false) {
      savedSchool.current = prevSaved;
      setForm(prev => ({ ...prev, ...prevForm, high_school: prevSaved }));
    }
  };

  const onSchoolChange = ({ name, city, state, zip }) => {
    if (schoolSaveTimer.current) clearTimeout(schoolSaveTimer.current);
    const typed = (name || '').trim();

    if (city || state || zip) {            // 1. picked from the list
      setSchoolWarning(null);
      persistSchool({
        high_school: name,
        high_school_city: city || '',
        high_school_state: state || '',
        high_school_zip: zip || '',
      });
      return;
    }
    if (!typed) {                          // clearing the field is always allowed
      setSchoolWarning(null);
      setLocal('high_school', '');
      // Clearing the school clears its city and zip too (the province stays).
      const cleared = { high_school: '', high_school_city: '', high_school_zip: '' };
      schoolSaveTimer.current = setTimeout(() => persistSchool(cleared), 700);
      return;
    }
    // The box shows exactly what was typed. Writing `typed` (trimmed) back here
    // is what made a trailing space impossible to enter.
    setLocal('high_school', name);         // 3. unrecognised -> held, not saved
    setSchoolWarning(typed);
  };

  const saveSchoolAnyway = () => {
    const name = schoolWarning;
    setSchoolWarning(null);
    // Free text keeps the PROVINCE (default: the athlete's address province) and,
    // when it replaces a different saved school, clears the old school's city and
    // zip (commit 876d3ab3: no "new name in the old city" hybrid). A first entry
    // keeps whatever city/zip are already on the form.
    if (!name) return;
    const patch = { high_school: name };
    if (savedSchool.current) {
      patch.high_school_city = '';
      patch.high_school_zip = '';
    }
    if (!form.high_school_state && form.state) patch.high_school_state = form.state;
    persistSchool(patch);
  };

  // ── Photos ──────────────────────────────────────────────────────────
  const uploadPhoto = async (slot, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file); fd.append('photo_type', 'player'); fd.append('slot', slot);
    try {
      const r = await axios.post(`${API_URL}/athletes/upload-photo`, fd, cfg());
      const field = slot === 2 ? 'player_photo_2' : slot === 3 ? 'player_photo_3' : 'player_photo';
      setLocal(field, r.data.filepath);
      reportStatus('Photo uploaded');
    } catch (e) { reportStatus(e.response?.data?.detail || 'Photo upload failed', true); }
  };
  const deletePhoto = async (slot) => {
    try {
      await axios.delete(`${API_URL}/athletes/delete-photo`, { ...cfg(), data: { slot } });
      const field = slot === 2 ? 'player_photo_2' : slot === 3 ? 'player_photo_3' : 'player_photo';
      setLocal(field, '');
      reportStatus('Photo removed');
    } catch (e) { reportStatus(e.response?.data?.detail || 'Delete failed', true); }
  };
  const setPrimaryPhoto = async (slot) => {
    try {
      await axios.post(`${API_URL}/athletes/set-primary-photo`, { slot }, cfg());
      setLocal('primary_photo_index', slot);
      reportStatus('Primary photo set');
    } catch (e) { reportStatus(e.response?.data?.detail || 'Failed', true); }
  };

  // ── Camp Photos (staff-captured; stored private, served presigned) ──────
  //
  // The two hand slots are the hand-size station's reference-mat angles — the
  // hand flat with fingers spread, and the same hand in side profile against
  // the printed mat. They are what makes a typed hand measurement re-checkable
  // later without re-measuring the athlete, so staff need to see them, but they
  // are evidence rather than media: they belong here on the admin panel and
  // deliberately NOT on the athlete's own MediaPage strip, which is for
  // headshot/full-body/action.
  const CAMP_SLOTS = [
    ['headshot', 'Camp Headshot'], ['full_body', 'Camp Full Body'], ['action', 'Camp Action'],
    ['hand', 'Hand on Mat'], ['hand_2', 'Hand Side Profile'],
  ];
  const uploadCampPhoto = async (slotKey, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file); fd.append('slot', slotKey);
    try {
      await axios.post(`${API_URL}/athletes/${userId}/camp-photo`, fd, cfg());
      await refreshCampPhotos();
      reportStatus('Camp photo uploaded');
    } catch (e) { reportStatus(e.response?.data?.detail || 'Camp photo upload failed', true); }
  };
  const deleteCampPhoto = async (slotKey) => {
    try {
      await axios.delete(`${API_URL}/athletes/${userId}/camp-photo`, { ...cfg(), data: { slot: slotKey } });
      await refreshCampPhotos();
      reportStatus('Camp photo removed');
    } catch (e) { reportStatus(e.response?.data?.detail || 'Delete failed', true); }
  };
  const campPhotoSlot = (slotKey, label) => {
    const raw = campPhotos[slotKey];
    // presigned camp URLs are on the regionless S3 host the CSP blocks — proxy them
    const url = raw ? `${API_URL}/image-proxy?url=${encodeURIComponent(raw)}` : null;
    return (
      <div key={slotKey} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, width: 150 }}>
        <div style={{ width: '100%', height: 120, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {url ? <img src={url} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: '#94a3b8', fontSize: 12 }}>Empty</span>}
        </div>
        <div style={{ fontSize: 11, color: '#64748b', margin: '6px 0' }}>{label}</div>
        <label className="pz-btn pz-btn--secondary pz-btn--md" style={{ display: 'block', textAlign: 'center', fontSize: 12, padding: '4px 0', cursor: 'pointer' }}>
          {raw ? 'Replace' : 'Upload'}
          <input type="file" accept="image/*" hidden onChange={e => { uploadCampPhoto(slotKey, e.target.files[0]); e.target.value = ''; }} />
        </label>
        {raw && (
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <Button variant="danger" size="sm" style={{ flex: 1 }} onClick={() => deleteCampPhoto(slotKey)}>Delete</Button>
          </div>
        )}
      </div>
    );
  };

  // ── Videos ──────────────────────────────────────────────────────────
  const nextSlot = (category) => {
    const slots = videos.filter(v => v.category === category).map(v => v.slot_number || 0);
    return (slots.length ? Math.max(...slots) : 0) + 1;
  };
  // Reports whether the add actually landed, so the caller can decide what to
  // do with the URL the user typed.
  const addVideoUrl = async (category, url) => {
    if (!url) return false;
    try {
      await axios.post(`${API_URL}/videos/`, { category, slot_number: nextSlot(category), video_url: url }, cfg());
      await refreshVideos(); reportStatus('Video added');
      return true;
    } catch (e) {
      reportStatus(e.response?.data?.detail || 'Add failed', true);
      return false;
    }
  };
  // Clears the draft only on success, so a failed add leaves the pasted URL in
  // the box to retry rather than silently discarding it.
  const submitVideoUrl = async (category) => {
    const url = (videoDrafts[category] || '').trim();
    if (!url) return;
    if (await addVideoUrl(category, url)) {
      setVideoDrafts(d => ({ ...d, [category]: '' }));
    }
  };
  const uploadVideo = async (category, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file); fd.append('category', category); fd.append('slot_number', nextSlot(category));
    try {
      await axios.post(`${API_URL}/videos/upload`, fd, cfg());
      await refreshVideos(); reportStatus('Video uploaded');
    } catch (e) { reportStatus(e.response?.data?.detail || 'Upload failed', true); }
  };
  const deleteVideo = async (id) => {
    try { await axios.delete(`${API_URL}/videos/${id}`, cfg()); await refreshVideos(); reportStatus('Video deleted'); }
    catch (e) { reportStatus(e.response?.data?.detail || 'Delete failed', true); }
  };

  // ── Documents ───────────────────────────────────────────────────────
  const uploadDoc = async (category, file) => {
    if (!file) return;
    const used = docs.map(d => d.slot_number || 0);
    let slot = 1; while (used.includes(slot) && slot < 8) slot += 1;
    const fd = new FormData();
    fd.append('file', file); fd.append('slot_number', slot); fd.append('category', category); fd.append('label', file.name);
    try {
      await axios.post(`${API_URL}/athletes/documents/upload`, fd, cfg());
      await refreshDocs(); reportStatus('Document uploaded');
    } catch (e) { reportStatus(e.response?.data?.detail || 'Upload failed', true); }
  };
  const deleteDoc = async (id) => {
    try { await axios.delete(`${API_URL}/athletes/documents/${id}`, cfg()); await refreshDocs(); reportStatus('Document deleted'); }
    catch (e) { reportStatus(e.response?.data?.detail || 'Delete failed', true); }
  };

  // ── Render helpers ──────────────────────────────────────────────────
  const text = (name, label, type = 'text', placeholder = '') => {
    const val = form[name];
    const state = (val === undefined || val === null || val === '') ? 'empty' : 'value';
    return (
      <EditorField label={label} state={state}>
        <input className="ae-field__input" type={type} value={val ?? ''}
          onChange={onText(name)} onBlur={onBlurSave(name)} placeholder={placeholder} />
      </EditorField>
    );
  };
  const multiPills = (name, options) => {
    const cur = Array.isArray(form[name]) ? form[name] : [];
    return (
      <div className="ed-row" style={{ flexWrap: 'wrap', gap: 8 }}>
        {options.map(o => (
          <Pill key={o.value} variant={cur.includes(o.value) ? 'active' : 'ghost'} as="button"
            onClick={() => toggleMulti(name, o.value)}>{o.label}</Pill>
        ))}
      </div>
    );
  };
  const singlePills = (name, options) => (
    <div className="ed-row" style={{ flexWrap: 'wrap', gap: 8 }}>
      {options.map(o => (
        <Pill key={o.value} variant={form[name] === o.value ? 'active' : 'ghost'} as="button"
          onClick={() => setSingle(name, o.value)}>{o.label}</Pill>
      ))}
    </div>
  );
  const photoSlot = (slot) => {
    const field = slot === 2 ? 'player_photo_2' : slot === 3 ? 'player_photo_3' : 'player_photo';
    const url = form[field];
    const isPrimary = (form.primary_photo_index || 1) === slot;
    return (
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, width: 150 }}>
        <div style={{ width: '100%', height: 120, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {url ? <img src={url} alt={`Slot ${slot}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: '#94a3b8', fontSize: 12 }}>Empty</span>}
        </div>
        <div style={{ fontSize: 11, color: '#64748b', margin: '6px 0' }}>Slot {slot}{isPrimary ? ' · PRIMARY' : ''}</div>
        <label className="pz-btn pz-btn--secondary pz-btn--md" style={{ display: 'block', textAlign: 'center', fontSize: 12, padding: '4px 0', cursor: 'pointer' }}>
          {url ? 'Replace' : 'Upload'}
          <input type="file" accept="image/*" hidden onChange={e => uploadPhoto(slot, e.target.files[0])} />
        </label>
        {url && (
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            {/* Zero side padding: two pz-btn--sm buttons at their default 14px
                padding are wider than the 130px slot and spill out of it. */}
            {!isPrimary && <Button variant="secondary" size="sm" style={{ flex: 1, minWidth: 0, padding: '6px 0' }} onClick={() => setPrimaryPhoto(slot)}>Primary</Button>}
            <Button variant="danger" size="sm" style={{ flex: 1, minWidth: 0, padding: '6px 0' }} onClick={() => deletePhoto(slot)}>Delete</Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="ae-editor-body">
      <SectionIndex
        sections={sections}
        activeId={view === 'measurables' ? null : activeSection}
        onJump={onJump}
        extraItems={[{ id: 'measurables', label: 'Measurables' }]}
        onExtra={() => setView('measurables')}
        activeExtra={view === 'measurables' ? 'measurables' : null}
      />

      {view === 'measurables' && (
        <MeasurablesLedger
          userId={userId} profile={form} perms={perms} apiUrl={API_URL}
          getConfig={getConfig} logs={logs}
          onChange={(patch) => { setForm(prev => ({ ...prev, ...patch })); if (onSaved) onSaved(patch); }}
          onSaved={() => reportStatus('Saved')}
        />
      )}

      <div className="ae-card" style={view === 'measurables' ? { display: 'none' } : undefined}>
        {/* Membership & Subscription */}
        {membership && (
          <section id="membership" className="ae-editor-section">
            <SectionCap label="Membership & Subscription" hint="CONCIERGE + COMP ACCESS" />
            {!canEditMembership && (
              <p className="ae-hint">Membership is managed by ProZpects staff.</p>
            )}
            <EditorField label="Concierge Athlete" state={membership.is_concierge ? 'value' : 'empty'}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 36 }}>
                <input type="checkbox" checked={!!membership.is_concierge} disabled={!canEditMembership}
                  onChange={e => postMembership({ is_concierge: e.target.checked })} style={{ accentColor: '#4D8064' }} /> Concierge member
              </label>
            </EditorField>
            {/* Same write path the Concierge page's own dropdown uses
                (PUT /concierge/:id/owner) — one selector, one endpoint, so
                the two surfaces can never disagree about who owns a client.
                A viewer who cannot edit membership (e.g. a partner) gets
                `eligible_owners: [self]` only, so a masked owner — the
                backend returns {user_id: null, name: 'ProZpects Staff'} when
                the real owner is outside the viewer's own list — matches no
                <option> in a <select> and silently reads as unassigned. Read
                the name directly instead, mirroring ConciergeNotesPage's own
                `owner?.name || '—'` read-only branch for the identical case. */}
            <Field label="Concierge Manager">
              {canEditMembership ? (
                <select className="ae-field__input" style={{ maxWidth: 320 }}
                  value={membership.concierge_owner?.user_id ?? ''} onChange={changeConciergeOwner}>
                  <option value="">— Unassigned —</option>
                  {(membership.eligible_owners || []).map(o => (
                    <option key={o.user_id} value={o.user_id}>
                      {o.email ? `${o.name} · ${o.email}` : o.name}{o.role === 'partner' ? ' (partner)' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="ae-field__input" style={{ maxWidth: 320 }} data-testid="membership-owner-readonly">
                  {membership.concierge_owner?.name || '—'}
                </div>
              )}
            </Field>
            {/* Same column as the Concierge page's "Contract end" field
                (users.concierge_expires_at) — named identically so editing it
                from either surface reads as one field, not two. */}
            <Field label="Contract End Date">
              <input className="ae-field__input" type="date" style={{ maxWidth: 200 }} disabled={!canEditMembership}
                value={contractEndDraft ?? (membership.concierge_expires_at || '')}
                onChange={e => setContractEndDraft(e.target.value)}
                onBlur={commitContractEnd} />
            </Field>
            <div style={{ fontSize: 13, color: '#475569', margin: '10px 0', padding: '10px 12px', background: '#f8fafc', borderRadius: 8 }}>
              <strong>Current subscription:</strong>{' '}
              {membership.subscription
                ? `${membership.subscription.payment_platform || '—'} · ${membership.subscription.plan_type || '—'} · ${membership.subscription.status}${membership.subscription.current_period_end ? ` · ends ${membership.subscription.current_period_end}` : ''}`
                : 'none'}{' '}
              <span style={{ color: membership.is_premium_active ? '#3F6B53' : '#b91c1c', fontWeight: 600 }}>
                {membership.is_premium_active ? '(active)' : '(inactive)'}
              </span>
            </div>
            {canEditMembership && (
              <Field label="Grant Comp Subscription" hint="Sets a complimentary sub with the end date below">
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input className="ae-field__input" type="date" style={{ maxWidth: 170 }} value={compEnd} onChange={e => setCompEnd(e.target.value)} />
                  <select className="ae-field__input" style={{ maxWidth: 150 }} value={compPlan} onChange={e => setCompPlan(e.target.value)}>
                    <option value="player_annual">Annual</option>
                    <option value="player_monthly">Monthly</option>
                  </select>
                  <Button variant="secondary" onClick={applyComp}>
                    Set comp subscription
                  </Button>
                </div>
              </Field>
            )}
          </section>
        )}

        {/* Contact */}
        <section id="contact" className="ae-editor-section">
          <SectionCap label="Contact & Address" hint="HOW COACHES REACH THEM" />
          <Field label="First Name">
            <input className="ae-field__input" data-testid="athlete-first-name" value={nameDraft.first}
              onChange={(e) => setNameDraft(d => ({ ...d, first: e.target.value }))} onBlur={saveName} />
          </Field>
          <Field label="Last Name">
            <input className="ae-field__input" data-testid="athlete-last-name" value={nameDraft.last}
              onChange={(e) => setNameDraft(d => ({ ...d, last: e.target.value }))} onBlur={saveName} />
          </Field>
          <Field label="Account Email (login)">
            <input className="ae-field__input" type="email" value={accountEmail}
              onChange={(e) => setAccountEmail(e.target.value)} onBlur={saveEmail}
              placeholder="user@example.com" />
          </Field>
          <Field label="Home Address">
            <AddressAutocomplete id="home_address" name="home_address" value={form.home_address || ''} className="ae-field__input"
              placeholder="Start typing an address..." onManualChange={onText('home_address')} onBlur={onBlurSave('home_address')}
              onSelect={(d) => {
                const { country, ...rest } = d;
                const patch = country ? d : rest; // never save an empty country
                setForm(prev => ({ ...prev, ...patch }));
                saveFields(patch);
              }} />
          </Field>
          {text('address_line_2', 'Apt / Suite / Unit')}
          {text('city', 'City')}
          <AddressRegionFields
            country={form.country}
            state={form.state}
            postal={form.zip_code}
            Field={RegionField}
            inputClass="ae-field__input"
            selectClass="ae-field__input"
            rowClass="ed-field__row"
            onChange={(v, meta) => {
              if (meta && meta.countryChanged) {
                setForm(prev => ({ ...prev, ...v }));
                saveFields({ country: v.country, state: v.state, zip_code: v.zip_code });
              } else {
                setForm(prev => ({ ...prev, state: v.state, zip_code: v.zip_code }));
              }
            }}
            onBlur={(name) => saveField(name, form[name] ?? '')}
          />
          {text('cell_phone', 'Cell Phone', 'tel')}
          <EditorField label="Gender" state={form.gender ? 'value' : 'empty'}>
            <select className="ae-field__input" value={form.gender || ''} onChange={e => setSingle('gender', e.target.value)}>
              <option value="">Select...</option><option value="Male">Male</option><option value="Female">Female</option>
            </select>
          </EditorField>
        </section>

        {/* Coach Contacts — one block per sport the athlete plays. A two-sport
            athlete has two different HS coaches, and club teams are per-sport. */}
        <section id="hs-coach" className="ae-editor-section">
          <SectionCap label="Coach Contacts" hint="PER SPORT — HS + CLUB" />
          {athleteSports.map(sportName => (
            <React.Fragment key={sportName}>
              <SectionCap label={sportName} hint="HIGH SCHOOL + CLUB COACH" />
              {isCatalogSport(sportName) ? (
                <>
                  {coachField(sportName, 'hs_coach_name', 'HS Coach Name')}
                  <div className="ed-field__row">
                    {coachField(sportName, 'hs_coach_email', 'HS Coach Email', 'email')}
                    {coachField(sportName, 'hs_coach_phone', 'HS Coach Phone', 'tel')}
                  </div>
                  {coachField(sportName, 'club_team_name', 'Club Team Name')}
                  {coachField(sportName, 'club_coach_name', 'Club Coach Name')}
                  <div className="ed-field__row">
                    {coachField(sportName, 'club_coach_email', 'Club Coach Email', 'email')}
                    {coachField(sportName, 'club_coach_phone', 'Club Coach Phone', 'tel')}
                  </div>
                </>
              ) : (
                <span className="ae-field__hint">
                  “{sportName}” isn't in our sport catalog yet, so a coach contact can't be saved for it.
                </span>
              )}
            </React.Fragment>
          ))}
        </section>

        {/* Guardians */}
        <section id="guardians" className="ae-editor-section">
          <SectionCap label="Guardians" hint="RECRUITING COMPLIANCE" />
          <div className="ed-field__row">{text('guardian1_name', 'Primary Name')}{text('guardian1_relation', 'Relation')}</div>
          <div className="ed-field__row">{text('guardian1_phone', 'Primary Phone', 'tel')}{text('guardian1_email', 'Primary Email', 'email')}</div>
          <div className="ed-field__row">{text('guardian2_name', 'Secondary Name')}{text('guardian2_relation', 'Relation')}</div>
          <div className="ed-field__row">{text('guardian2_phone', 'Secondary Phone', 'tel')}{text('guardian2_email', 'Secondary Email', 'email')}</div>
        </section>

        {/* Academics + Athletics (merged) */}
        <section id="academics" className="ae-editor-section">
          <SectionCap label="Academics" hint="SCHOOL & ELIGIBILITY" />
          {/* High school XOR college. SchoolLevelSection owns the toggle and the
              college + eligibility save rules (the server requires both to
              switch); the high school side stays this editor's guarded picker. */}
          <SchoolLevelSection
            profile={form}
            onSave={saveSchoolLevel}
            confirmLeaveCollege={confirmLeaveCollege}
            toggleLabel="Plays in college"
            saveHint="Choose a college and years of eligibility to save."
            highSchool={(
              <Field label="High School">
                <SchoolPicker
                  value={form.high_school}
                  city={form.high_school_city}
                  state={form.high_school_state}
                  zip={form.high_school_zip}
                  onChange={onSchoolChange}
                  preferState={form.state}
                  role="player"
                  placeholder="Search high school…"
                  className="ae-field__input"
                />
                {schoolWarning && (
                  <div className="ae-school-warn" role="alert" data-testid="school-not-found">
                    <strong>Not saved yet.</strong>{' '}
                    “{schoolWarning}” is only typed text. Pick a suggestion to save
                    it, or keep it as typed if this school really is missing.
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={saveSchoolAnyway}
                      data-testid="school-save-anyway"
                    >
                      Save anyway
                    </Button>
                  </div>
                )}
              </Field>
            )}
          />
          <div className="ed-field__row">{text('graduation_year', 'Graduation Year', 'number')}{text('ncaa_eligibility_id', 'NCAA Eligibility ID')}</div>
          <EditorField label="Honors" state={form.honors ? 'value' : 'empty'}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 36 }}>
              <input type="checkbox" checked={!!form.honors} onChange={e => setSingle('honors', e.target.checked)} style={{ accentColor: '#4D8064' }} /> Honors student
            </label>
          </EditorField>

          {/* GPA / ACT / SAT — verifiable, so they carry seals and drive the
              same save brain as the measurables ledger rather than the plain
              autosave the fields above use. */}
          <SectionCap label="Scores" hint="GPA & TEST SCORES — CLICK A SEAL TO VERIFY" />
          <AcademicScoresFields
            userId={userId} profile={form} perms={perms} apiUrl={API_URL}
            getConfig={getConfig} logs={logs}
            onChange={(patch) => { setForm(prev => ({ ...prev, ...patch })); if (onSaved) onSaved(patch); }}
            onSaved={() => reportStatus('Saved')}
          />

          <SectionCap label="Athletics" hint="SPORT & POSITIONS" />
          <EditorField label="Sport" state={form.sport ? 'value' : 'empty'}>
            <select className="ae-field__input" value={form.sport ? currentSportSlug : ''}
              onChange={e => handleSportChange(e.target.value)}>
              {/* currentSportSlug falls back to football for an unset sport, so
                  bind to '' instead — otherwise the select would show Football
                  for an athlete who has no sport saved. */}
              {!form.sport && <option value="">Select sport...</option>}
              {catalog.sports.map(s => <option key={s.slug} value={s.slug}>{s.name}</option>)}
            </select>
          </EditorField>
          <div className="ed-field__row">
            {positionSelect('Primary Position', 'position_primary')}
            {positionSelect('Secondary Position', 'position_secondary')}
          </div>
          <div className="ed-field__row">
            {positionSelect('Other Position', 'position_other')}
            {text('jersey_number', 'Jersey #', 'number')}
          </div>
          <SectionCap label="Other Sports" hint="UP TO THREE — MATCHES THE MOBILE EDITOR" />
          <div className="ed-field__row">
            {otherSportSelect(0)}
            {otherSportSelect(1)}
          </div>
          <div className="ed-field__row">
            {otherSportSelect(2)}
          </div>
        </section>

        {/* Recruiting Preferences */}
        <section id="recruiting" className="ae-editor-section">
          <SectionCap label="Recruiting Preferences" hint="WHAT THEY'RE LOOKING FOR" />
          <Field label="Intended Majors">{multiPills('major_preferences', MAJORS)}</Field>
          {text('majors_other', 'Other Majors')}
          <Field label="College Region">{multiPills('college_region_preferences', REGIONS)}</Field>
          <Field label="Division">{multiPills('division_preferences', DIVISIONS)}</Field>
          <Field label="School Size">{multiPills('school_size_preferences', SCHOOL_SIZES)}</Field>
          <Field label="Campus Setting">{multiPills('campus_setting_preferences', CAMPUS_SETTINGS)}</Field>
          <Field label="Public or Private">{singlePills('school_type_preference', SCHOOL_TYPES)}</Field>
          <Field label="Hobbies">{multiPills('hobbies', HOBBIES)}</Field>
          {text('hobbies_other', 'Other Hobbies')}
          {/* College offers, campus visits & commitment — same editor as the
              read-view card, editable here in the Edit panel too. */}
          <OffersVisitsCard
            userId={userId}
            profile={initialProfile}
            canEdit={perms.canEditProfile}
            apiUrl={API_URL}
            getConfig={getConfig}
            onChange={(patch) => { if (onSaved) onSaved(patch); }}
          />
        </section>

        {/* Social & Personal Statement */}
        <section id="social" className="ae-editor-section">
          <SectionCap label="Social & Personal Statement" hint="PUBLIC-FACING" />
          <Field label="Personal Statement">
            <textarea className="ae-field__input" rows={3} value={form.personal_statement || ''} onChange={onText('personal_statement')} onBlur={onBlurSave('personal_statement')} />
          </Field>
          {text('hudl_handle', 'Hudl', 'text', '12345678/jordan-smith')}
          <div className="ed-field__row" style={{ flexWrap: 'wrap' }}>
            {SOCIALS.map(([name, label]) => <React.Fragment key={name}>{text(name, label)}</React.Fragment>)}
          </div>
        </section>

        {/* Photos & Video (merged) */}
        <section id="photos" className="ae-editor-section">
          <SectionCap label="Photos" hint="HEADSHOT + ACTION" />
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>{[1, 2, 3].map(photoSlot)}</div>
          <div style={{ fontSize: 11, color: '#64748b', margin: '14px 0 6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Camp Photos</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>{CAMP_SLOTS.map(([k, l]) => campPhotoSlot(k, l))}</div>
          <div style={{ fontSize: 11, color: '#64748b', margin: '14px 0 6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Trading Cards</div>
          {/* Read-only: AI-given and athlete-built cards. Only the athlete can
              build or delete a card (in the app). */}
          {cards.length === 0 ? (
            <div style={{ fontSize: 13, color: '#94a3b8' }}>None built yet</div>
          ) : (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }} data-testid="editor_trading_cards">
              {cards.map(card => {
                const label = cardLabel(card);
                const src = card.composite_url ? `${API_URL}/image-proxy?url=${encodeURIComponent(card.composite_url)}` : null;
                return (
                  <div key={card.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, width: 150, boxSizing: 'border-box' }}>
                    <div style={{ width: '100%', height: 120, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {src
                        ? <a href={card.composite_url} target="_blank" rel="noopener noreferrer" style={{ width: '100%', height: '100%' }}><img src={src} alt={label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} /></a>
                        : <span style={{ color: '#94a3b8', fontSize: 12 }}>No image</span>}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>{label}{card.is_primary ? ' · PRIMARY' : ''}</div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ marginTop: 20 }}>
            <SectionCap label="Videos" hint="UPLOAD OR LINK" />
            {VIDEO_CATEGORIES.map(([cat, label]) => {
              const vids = videos.filter(v => v.category === cat);
              return (
                <div key={cat} style={{ marginBottom: 14 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a', marginBottom: 6 }}>{label}</div>
                  {vids.map(v => (
                    <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#475569', marginBottom: 4 }}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.label || v.video_title || v.video_url}</span>
                      <Button variant="danger" size="sm" onClick={() => deleteVideo(v.id)}>Delete</Button>
                    </div>
                  ))}
                  {/* The URL field used to commit on Enter only, with no
                      button — which reads as "adding a video is broken". */}
                  <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input className="ae-field__input" style={{ flex: 1, minWidth: 200 }} placeholder="Paste YouTube/Hudl URL…"
                      aria-label={`${label} video URL`}
                      value={videoDrafts[cat] || ''}
                      onChange={e => setVideoDrafts(d => ({ ...d, [cat]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') submitVideoUrl(cat); }} />
                    <Button variant="secondary" size="sm" disabled={!(videoDrafts[cat] || '').trim()}
                      onClick={() => submitVideoUrl(cat)}>
                      Add
                    </Button>
                    <label className="pz-btn pz-btn--secondary pz-btn--md" style={{ fontSize: 12, padding: '6px 10px', cursor: 'pointer' }}>
                      Upload file<input type="file" accept="video/*" hidden onChange={e => { uploadVideo(cat, e.target.files[0]); e.target.value = ''; }} />
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Documents */}
        <section id="documents" className="ae-editor-section">
          <SectionCap label="Documents" hint="TRANSCRIPT / TEST SCORES" />
          {docs.map(d => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#475569', marginBottom: 4 }}>
              <span style={{ flex: 1 }}>{(d.category || 'doc')}: {d.label || d.filename}</span>
              <Button variant="danger" size="sm" onClick={() => deleteDoc(d.id)}>Delete</Button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            {DOC_CATEGORIES.map(([cat, label]) => (
              <label key={cat} className="pz-btn pz-btn--secondary pz-btn--md" style={{ fontSize: 12, padding: '6px 10px', cursor: 'pointer' }}>
                Upload {label}
                <input type="file" hidden onChange={e => { uploadDoc(cat, e.target.files[0]); e.target.value = ''; }} />
              </label>
            ))}
          </div>

        </section>

        {/* Accolades — the same editor the athlete uses, addressed at this
            athlete. Staff could previously only toggle an accolade's seal
            here; there was no way to create one. */}
        <section id="accolades" className="ae-editor-section">
          <SectionCap label="Accolades" hint="AWARDS · RANKINGS · SELECTIONS" />
          <AccoladesEditor sportName={form.sport} getConfig={cfg} />
        </section>

        {/* Stats — which ledger an athlete keeps is a property of their sport,
            so this routes off the catalog exactly as iOS's
            ProfileSectionDestination does. Equestrian is the one named slug:
            it keeps a show record plus credentials rather than a stats_mode
            ledger, which is how the read-view cards special-case it too. */}
        <section id="stats" className="ae-editor-section">
          <SectionCap label={statsSectionLabel} hint="SPORT-SPECIFIC LEDGER" />
          {statsEditor}
        </section>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <EditorialButton variant="solid" onClick={onClose}>Done editing</EditorialButton>
        </div>
      </div>
    </div>
  );
}
